'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { ArrowRight, CheckCircle, Zap, Globe, Shield, BarChart3, Smartphone, CreditCard, MapPin, Star, ChevronDown, Menu, X, Play } from 'lucide-react';
import { faqs } from '@/data/faqs';

const plans = [
  {
    name: 'Starter',
    price: '£49',
    period: '/mo',
    description: 'Perfect for small operators launching their first branded app.',
    drivers: 'Up to 10 drivers',
    highlight: false,
    features: [
      'Passenger PWA app',
      'Driver PWA app',
      'Back office dashboard',
      'Real-time dispatch',
      'Stripe payments',
      'Basic analytics',
      'Email support',
    ],
  },
  {
    name: 'Professional',
    price: '£149',
    period: '/mo',
    description: 'For growing fleets that need advanced tools and deeper insights.',
    drivers: 'Up to 50 drivers',
    highlight: true,
    features: [
      'Everything in Starter',
      'Custom domain & SSL',
      'Zone-based pricing',
      'Promo codes',
      'Advanced reports',
      'Staff accounts',
      'Priority support',
    ],
  },
  {
    name: 'Enterprise',
    price: '£349',
    period: '/mo',
    description: 'Unlimited scale with dedicated support and custom integrations.',
    drivers: 'Unlimited drivers',
    highlight: false,
    features: [
      'Everything in Professional',
      'Unlimited drivers & bookings',
      'White-label mobile apps',
      'Custom integrations',
      'SLA guarantee',
      'Dedicated account manager',
      'API access',
    ],
  },
];

const caseStudies = [
  {
    company: 'City Cabs London',
    location: 'London, UK',
    result: '340% increase in online bookings',
    quote: 'We launched our branded app in 18 minutes. Our drivers love the simplicity and passengers keep coming back.',
    role: 'Operations Director',
    drivers: 47,
    bookings: '2,800/mo',
  },
  {
    company: 'Dubai Executive Transfers',
    location: 'Dubai, UAE',
    result: '£180k revenue in first 6 months',
    quote: 'The white-label setup meant we could pitch corporate clients with our own brand from day one. Game changer.',
    role: 'Founder',
    drivers: 23,
    bookings: '1,200/mo',
  },
  {
    company: 'Melbourne Airport Rides',
    location: 'Melbourne, AU',
    result: '92% driver retention rate',
    quote: 'The driver app is so clean that our drivers actually prefer it over the big platforms. That says everything.',
    role: 'Fleet Manager',
    drivers: 31,
    bookings: '1,900/mo',
  },
];

const steps = [
  { num: '01', title: 'Sign up & choose a plan', body: 'Pick Starter, Professional, or Enterprise. Enter your business details.' },
  { num: '02', title: 'Platform provisioned in 20 min', body: 'Back office, passenger app, driver app, and demo data — all live instantly.' },
  { num: '03', title: 'Add your brand & domain', body: 'Upload your logo, set your colours, point your domain. Auto-SSL included.' },
  { num: '04', title: 'Add drivers & go live', body: 'Invite drivers, set your fares and zones, connect Stripe. Start taking bookings.' },
];

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--foreground)' }}>
      {/* NAV */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? 'rgba(10,10,10,0.92)' : 'transparent',
          backdropFilter: scrolled ? 'blur(12px)' : 'none',
          borderBottom: scrolled ? '1px solid var(--border)' : 'none',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm"
              style={{ background: 'var(--primary)', color: '#000' }}
            >
              R
            </div>
            <span className="font-bold text-base tracking-tight" style={{ color: 'var(--foreground)' }}>
              RideOS
            </span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            {[
              { label: 'Features', href: '#features' },
              { label: 'Pricing', href: '#pricing' },
              { label: 'Case Studies', href: '#case-studies' },
              { label: 'FAQ', href: '#faq' },
              { label: 'Demo', href: '/demo' },
            ]?.map((item) => (
              <a
                key={item?.label}
                href={item?.href}
                className="text-sm transition-colors"
                style={{ color: item?.label === 'Demo' ? 'var(--primary)' : 'var(--muted-foreground)' }}
              >
                {item?.label}
              </a>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-3">
            <Link href="/sign-up-login-screen" className="text-sm px-4 py-2 rounded-lg" style={{ color: 'var(--muted-foreground)' }}>
              Sign in
            </Link>
            <Link
              href="/sign-up-login-screen"
              className="text-sm px-4 py-2 rounded-lg font-semibold"
              style={{ background: 'var(--primary)', color: '#000' }}
            >
              Start free trial
            </Link>
          </div>
          <button className="md:hidden p-2 rounded-lg" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden px-6 pb-6 pt-2 space-y-3" style={{ background: 'rgba(10,10,10,0.97)', borderBottom: '1px solid var(--border)' }}>
            {[
              { label: 'Features', href: '#features' },
              { label: 'Pricing', href: '#pricing' },
              { label: 'Case Studies', href: '#case-studies' },
              { label: 'FAQ', href: '#faq' },
              { label: 'Demo', href: '/demo' },
            ]?.map((item) => (
              <a key={item?.label} href={item?.href} className="block text-sm py-2" style={{ color: item?.label === 'Demo' ? 'var(--primary)' : 'var(--muted-foreground)' }} onClick={() => setMobileMenuOpen(false)}>
                {item?.label}
              </a>
            ))}
            <Link href="/sign-up-login-screen" className="block text-sm px-4 py-2.5 rounded-lg font-semibold text-center mt-2" style={{ background: 'var(--primary)', color: '#000' }} onClick={() => setMobileMenuOpen(false)}>
              Start free trial
            </Link>
          </div>
        )}
      </nav>
      <main>
        {/* HERO */}
        <section id="hero" aria-label="Hero section" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20" style={{ background: 'radial-gradient(circle, var(--primary) 0%, transparent 70%)' }} />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-3xl opacity-10" style={{ background: 'radial-gradient(circle, #3b82f6 0%, transparent 70%)' }} />
            <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, var(--border) 1px, transparent 0)', backgroundSize: '40px 40px' }} />
          </div>
          <div className="relative max-w-5xl mx-auto px-6 text-center">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-8 border"
              style={{ background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.25)', color: 'var(--primary)' }}
            >
              <Zap className="w-3 h-3" />
              Launch your branded ride-hailing platform in 20 minutes
            </div>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6" style={{ color: 'var(--foreground)' }}>
              Your taxi brand.
              <br />
              <span style={{ color: 'var(--primary)' }}>Your app.</span>
              <br />
              Your revenue.
            </h1>
            <p className="text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
              RideOS gives taxi companies, airport transfer operators, and chauffeur businesses a fully white-label platform — passenger app, driver app, back office — under their own brand.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link href="/sign-up-login-screen" className="flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-base transition-all hover:opacity-90" style={{ background: 'var(--primary)', color: '#000' }}>
                Start your free trial
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#case-studies" className="flex items-center gap-2 px-8 py-4 rounded-xl font-medium text-base border" style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}>
                <Play className="w-4 h-4" />
                See it in action
              </a>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-8 mt-14">
              {[
                { val: '500+', label: 'Active operators' },
                { val: '2M+', label: 'Bookings processed' },
                { val: '20 min', label: 'Average setup time' },
                { val: '99.9%', label: 'Uptime SLA' },
              ]?.map((stat) => (
                <div key={stat?.label} className="text-center">
                  <p className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>{stat?.val}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{stat?.label}</p>
                </div>
              ))}
            </div>
          </div>
          <a href="#how-it-works" className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce" style={{ color: 'var(--muted-foreground)' }}>
            <ChevronDown className="w-5 h-5" />
          </a>
        </section>
      </main>
      {/* HOW IT WORKS */}
      <section className="py-24 px-6" id="how-it-works">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--primary)' }}>How it works</p>
            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: 'var(--foreground)' }}>From sign-up to live bookings in 4 steps</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps?.map((step, i) => (
              <div key={step?.num} className="p-6 rounded-2xl border" style={{ background: 'var(--card)', borderColor: i === 0 ? 'rgba(245,158,11,0.4)' : 'var(--border)' }}>
                <div className="text-4xl font-black mb-4 leading-none" style={{ color: i === 0 ? 'var(--primary)' : 'rgba(255,255,255,0.08)' }}>{step?.num}</div>
                <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--foreground)' }}>{step?.title}</h3>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{step?.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* FEATURES BENTO */}
      <section id="features" aria-label="Platform features" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--primary)' }}>Platform features</p>
            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: 'var(--foreground)' }}>Everything your fleet needs</h2>
            <p className="text-base mt-4 max-w-xl mx-auto" style={{ color: 'var(--muted-foreground)' }}>
              One platform. Passenger app, driver app, back office, payments, analytics — all under your brand.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 p-8 rounded-2xl border relative overflow-hidden" style={{ background: 'var(--card)', borderColor: 'rgba(245,158,11,0.25)' }}>
              <div className="absolute top-0 right-0 w-48 h-48 rounded-full blur-3xl opacity-10 pointer-events-none" style={{ background: 'var(--primary)' }} />
              <Globe className="w-8 h-8 mb-4" style={{ color: 'var(--primary)' }} />
              <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--foreground)' }}>Your Brand, Your Domain</h3>
              <p className="text-sm leading-relaxed mb-6" style={{ color: 'var(--muted-foreground)' }}>
                Point your domain to RideOS in minutes. Auto-provisioned SSL, subdomain for back office, passenger app, and driver app — all under your brand. Entri-style auto-connect for GoDaddy, Namecheap, Cloudflare, and more.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Custom domain', 'Auto SSL', 'Passenger app', 'Driver app', 'Back office']?.map((tag) => (
                  <span key={tag} className="text-xs px-2.5 py-1 rounded-full border" style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}>{tag}</span>
                ))}
              </div>
            </div>
            <div className="p-6 rounded-2xl border" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
              <Shield className="w-6 h-6 mb-4" style={{ color: 'var(--primary)' }} />
              <h3 className="text-base font-bold mb-2" style={{ color: 'var(--foreground)' }}>Multi-Tenant Isolation</h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                Every subscriber's data — drivers, passengers, bookings, fares — is fully isolated by company_id. No data leakage, ever. Row-level security enforced at the database layer.
              </p>
            </div>
            <div className="p-6 rounded-2xl border" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
              <Smartphone className="w-6 h-6 mb-4" style={{ color: 'var(--primary)' }} />
              <h3 className="text-base font-bold mb-2" style={{ color: 'var(--foreground)' }}>PWA Passenger & Driver Apps</h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                No app store needed. Passengers book and drivers accept jobs from any browser. Install to home screen in one tap.
              </p>
            </div>
            <div className="p-6 rounded-2xl border" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
              <Zap className="w-6 h-6 mb-4" style={{ color: 'var(--primary)' }} />
              <h3 className="text-base font-bold mb-2" style={{ color: 'var(--foreground)' }}>Live Dispatch in Real-Time</h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                Supabase real-time pushes booking assignments to drivers instantly. Zero polling, zero lag.
              </p>
            </div>
            <div className="p-6 rounded-2xl border" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
              <CreditCard className="w-6 h-6 mb-4" style={{ color: 'var(--primary)' }} />
              <h3 className="text-base font-bold mb-2" style={{ color: 'var(--foreground)' }}>Stripe-Powered Payments</h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                Accept card payments, process refunds, and manage subscription billing — all automated via Stripe webhooks.
              </p>
            </div>
            <div className="md:col-span-2 p-6 rounded-2xl border" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
              <BarChart3 className="w-6 h-6 mb-4" style={{ color: 'var(--primary)' }} />
              <h3 className="text-base font-bold mb-2" style={{ color: 'var(--foreground)' }}>Revenue & Performance Reports</h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                Track revenue, bookings, cancellation rates, and driver performance. Export to CSV. Filter by any date range. Real-time alert feed for booking assignments, payment failures, and driver status changes.
              </p>
            </div>
            <div className="p-6 rounded-2xl border" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
              <MapPin className="w-6 h-6 mb-4" style={{ color: 'var(--primary)' }} />
              <h3 className="text-base font-bold mb-2" style={{ color: 'var(--foreground)' }}>Zone-Based Pricing</h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                Define service areas, airport zones, surge zones. Set per-zone base fares and per-km rates.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* PRICING */}
      <section className="py-24 px-6" id="pricing">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--primary)' }}>Pricing</p>
            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: 'var(--foreground)' }}>Simple, transparent pricing</h2>
            <p className="text-base mt-4" style={{ color: 'var(--muted-foreground)' }}>14-day free trial on all plans. No credit card required.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans?.map((plan) => (
              <div
                key={plan?.name}
                className="relative p-8 rounded-2xl border flex flex-col"
                style={{
                  background: plan?.highlight ? 'rgba(245,158,11,0.06)' : 'var(--card)',
                  borderColor: plan?.highlight ? 'var(--primary)' : 'var(--border)',
                  boxShadow: plan?.highlight ? '0 0 40px rgba(245,158,11,0.12)' : 'none',
                }}
              >
                {plan?.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold" style={{ background: 'var(--primary)', color: '#000' }}>
                    Most popular
                  </div>
                )}
                <div className="mb-6">
                  <h3 className="text-lg font-bold mb-1" style={{ color: 'var(--foreground)' }}>{plan?.name}</h3>
                  <p className="text-xs mb-4" style={{ color: 'var(--muted-foreground)' }}>{plan?.description}</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-black" style={{ color: 'var(--foreground)' }}>{plan?.price}</span>
                    <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{plan?.period}</span>
                  </div>
                  <p className="text-xs mt-2 font-medium" style={{ color: 'var(--primary)' }}>{plan?.drivers}</p>
                </div>
                <ul className="space-y-3 flex-1 mb-8">
                  {plan?.features?.map((f) => (
                    <li key={f} className="flex items-center gap-2.5">
                      <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--success)' }} />
                      <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  href="/sign-up-login-screen"
                  className="block text-center py-3 rounded-xl font-semibold text-sm transition-all"
                  style={{
                    background: plan?.highlight ? 'var(--primary)' : 'transparent',
                    color: plan?.highlight ? '#000' : 'var(--foreground)',
                    border: plan?.highlight ? 'none' : '1px solid var(--border)',
                  }}
                >
                  Start free trial
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CASE STUDIES */}
      <section className="py-24 px-6" id="case-studies">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--primary)' }}>Case studies</p>
            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: 'var(--foreground)' }}>Operators who scaled with RideOS</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {caseStudies?.map((cs) => (
              <div key={cs?.company} className="p-7 rounded-2xl border flex flex-col" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: 5 })?.map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-current" style={{ color: 'var(--primary)' }} />
                  ))}
                </div>
                <p className="text-sm leading-relaxed mb-6 flex-1" style={{ color: 'var(--muted-foreground)' }}>"{cs?.quote}"</p>
                <div className="pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                  <p className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{cs?.company}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{cs?.role} · {cs?.location}</p>
                  <div className="flex gap-4 mt-3">
                    <div>
                      <p className="text-base font-bold" style={{ color: 'var(--primary)' }}>{cs?.drivers}</p>
                      <p className="text-2xs" style={{ color: 'var(--muted-foreground)' }}>drivers</p>
                    </div>
                    <div>
                      <p className="text-base font-bold" style={{ color: 'var(--primary)' }}>{cs?.bookings}</p>
                      <p className="text-2xs" style={{ color: 'var(--muted-foreground)' }}>bookings</p>
                    </div>
                  </div>
                  <p className="text-xs font-semibold mt-3 px-2.5 py-1 rounded-full inline-block" style={{ background: 'rgba(16,185,129,0.1)', color: 'var(--success)' }}>
                    {cs?.result}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* FAQ SECTION */}
      <section id="faq" aria-label="Frequently asked questions" className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--primary)' }}>FAQ</p>
            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: 'var(--foreground)' }}>Frequently asked questions</h2>
          </div>
          <div className="space-y-4">
            {faqs?.map((faq) => (
              <details key={faq?.question} className="group p-6 rounded-2xl border cursor-pointer transition-all" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
                <summary className="flex items-center justify-between font-semibold" style={{ color: 'var(--foreground)' }}>
                  {faq?.question}
                  <span className="text-xl transition-transform group-open:rotate-180" style={{ color: 'var(--primary)' }}>+</span>
                </summary>
                <p className="mt-4 text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                  {faq?.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section id="cta" aria-label="Call to action" className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="p-12 rounded-3xl border relative overflow-hidden" style={{ background: 'var(--card)', borderColor: 'rgba(245,158,11,0.3)' }}>
            <div className="absolute inset-0 opacity-5 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, var(--primary) 0%, transparent 70%)' }} />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--foreground)' }}>Ready to launch your brand?</h2>
              <p className="text-base mb-8" style={{ color: 'var(--muted-foreground)' }}>
                Join 500+ operators who launched their own ride-hailing platform with RideOS. 14-day free trial, no credit card required.
              </p>
              <Link
                href="/sign-up-login-screen"
                className="inline-flex items-center gap-2 px-10 py-4 rounded-xl font-bold text-base transition-all hover:opacity-90"
                style={{ background: 'var(--primary)', color: '#000' }}
              >
                Start your free trial
                <ArrowRight className="w-5 h-5" />
              </Link>
              <p className="text-xs mt-4" style={{ color: 'var(--muted-foreground)' }}>
                Setup in 20 minutes · No credit card · Cancel anytime
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* FOOTER */}
      <footer id="footer" aria-label="Footer" className="border-t py-12 px-6" style={{ borderColor: 'var(--border)' }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs" style={{ background: 'var(--primary)', color: '#000' }}>R</div>
            <span className="font-bold text-sm" style={{ color: 'var(--foreground)' }}>RideOS</span>
          </div>
          <div className="flex flex-wrap items-center gap-6">
            {['Privacy', 'Terms', 'Docs', 'Status', 'Contact']?.map((item) => (
              <a key={item} href="#" className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{item}</a>
            ))}
          </div>
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>© 2026 RideOS. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
